import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { showBulkActionToast } from "@/lib/bulkActionToast";
import { formatDate } from "@/lib/formatDate";
import { PAID_SOURCE_LABEL } from "@/lib/bankText";
import useHighlightParam from "@/hooks/useHighlightParam";
import { BillPaymentPlan } from "@/components/bills/BillPaymentPlan";
import { PaymentPlanDialog } from "@/components/bills/PaymentPlanDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Receipt, Send, Trash2, ServerCog, Shield } from "lucide-react";
import { ListSearch, matchesSearch } from "@/components/ListSearch";
import { StatusFilterPills, useStatusPills } from "@/components/StatusFilterPills";
import { PaymentApprovalPanel } from "@/components/bills/PaymentApprovalPanel";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { PaymentRunWizard } from "@/components/PaymentRunWizard";
import { BatchDetailModal } from "@/components/BatchDetailModal";
import { BillRowActions, BillPreviewDialog, useBillPreview } from "@/components/bills/BillAttachments";
import { BillAttachmentUploadDialog } from "@/components/bills/BillAttachmentUploadDialog";

export function Bills() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const billFilter = useStatusPills("bills.statusFilter", localStorage.getItem("demoCompanyId"));
  const BILL_STATUS = [["all","Alle"],["draft","Entwurf"],["open","Offen"],["posted","Verbucht"],["partially_paid","Teilbezahlt"],["paid","Bezahlt"],["overdue","Überfällig"]];
  const [billSearch, setBillSearch] = useState("");
  const visibleBills = billFilter.value === "all" ? bills : bills.filter((b) => b.status === billFilter.value);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    supplier_id: "",
    bill_number: "",
    issue_date: "",
    due_date: "",
    currency: "CHF",
    vat_rate: 7.7,
    line_items: [
      {
        description: "",
        amount: "",
        account_id: "",
      },
    ],
    subtotal: 0,
    tax_amount: 0,
    total: 0,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const billPreview = useBillPreview();
  const [planBill, setPlanBill] = useState(null);
  const [attachBill, setAttachBill] = useState(null);
  const replaceBill = (updated) => {
    if (!updated) return;
    setBills((prev) => prev.map((x) => (x._id === updated._id ? { ...x, ...updated } : x)));
  };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paymentBill, setPaymentBill] = useState(null);
  const [paymentIdemKey, setPaymentIdemKey] = useState(() => crypto.randomUUID());
  const [paymentForm, setPaymentForm] = useState({
    payment_date: "",
    amount: "",
    bank_account_id: "",
  });
  const [error, setError] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailBatchId, setDetailBatchId] = useState(null);

  const companyId = localStorage.getItem("demoCompanyId");

  const loadSuppliersAndAccounts = async () => {
    if (!companyId) return;
    try {
      const [supRes, accRes] = await Promise.all([
        apiClient.get(`/companies/${companyId}/contacts`, { params: { type: "supplier" } }),
        apiClient.get(`/companies/${companyId}/accounts`),
      ]);
      setSuppliers(supRes.data || []);
      setAccounts(accRes.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadBills = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(`/companies/${companyId}/bills`);
      setBills(res.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden der Eingangsrechnungen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuppliersAndAccounts();
    loadBills();
  }, []);

  useHighlightParam(bills, (b) => b._id || b.id);

  const recalcTotals = (draft) => {
    const lineSum = draft.line_items.reduce(
      (sum, li) => sum + (parseFloat(li.amount) || 0),
      0,
    );
    const vatRate = parseFloat(draft.vat_rate) || 0;
    const taxAmount = Math.round(lineSum * vatRate) / 100;
    const total = lineSum + taxAmount;
    return {
      ...draft,
      subtotal: lineSum,
      tax_amount: taxAmount,
      total,
    };
  };

  const handleLineChange = (index, field, value) => {
    const items = [...form.line_items];
    items[index] = {
      ...items[index],
      [field]: value,
    };
    const next = recalcTotals({ ...form, line_items: items });
    setForm(next);
  };

  const addLineItem = () => {
    const next = recalcTotals({
      ...form,
      line_items: [
        ...form.line_items,
        { description: "", amount: "", account_id: "" },
      ],
    });
    setForm(next);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    if (!form.supplier_id) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        supplier_id: form.supplier_id,
        bill_number: form.bill_number,
        issue_date: form.issue_date,
        due_date: form.due_date,
        currency: form.currency,
        vat_rate: parseFloat(form.vat_rate) || 0,
        line_items: form.line_items.map((li) => ({
          description: li.description,
          amount: parseFloat(li.amount) || 0,
          account_id: li.account_id,
        })),
        subtotal: form.subtotal,
        tax_amount: form.tax_amount,
        total: form.total,
        status: "open",
      };
      const res = await apiClient.post(
        `/companies/${companyId}/bills`,
        payload,
      );
      setBills((prev) => [res.data, ...prev]);
      setDialogOpen(false);
      setForm({
        supplier_id: "",
        bill_number: "",
        issue_date: "",
        due_date: "",
        currency: "CHF",
        vat_rate: 7.7,
        line_items: [
          {
            description: "",
            amount: "",
            account_id: "",
          },
        ],
        subtotal: 0,
        tax_amount: 0,
        total: 0,
      });
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Anlegen der Eingangsrechnung.");
    } finally {
      setSaving(false);
    }
  };

  const handlePostBill = async (billId) => {
    const bill = bills.find((x) => x._id === billId);
    if (bill?.delivery_check?.status === "deviation") {
      const ok = window.confirm(
        `Achtung: Lieferschein-Abgleich meldet ${bill.delivery_check.deviations || ""} Abweichung(en) zwischen Lieferschein und Rechnung (${bill.delivery_check.message}).\n\nTrotzdem buchen?`
      );
      if (!ok) return;
    }
    setPosting(true);
    setError("");
    try {
      await apiClient.post(`/bills/${billId}/post`, { description: null });
      await loadBills();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Buchen der Eingangsrechnung.");
    } finally {
      setPosting(false);
    }
  };

  const deleteBill = async (bill) => {
    const isPosted = bill.status && bill.status !== "draft";
    const msg = isPosted
      ? `Eingangsrechnung "${bill.bill_number || ""}" ist verbucht (Status: ${bill.status}).\n\nLöschen entfernt auch alle verknüpften Buchungen. Fortfahren?`
      : `Eingangsrechnung "${bill.bill_number || ""}" wirklich löschen?`;
    if (!window.confirm(msg)) return;
    try {
      const url = isPosted ? `/bills/${bill._id}?force=true` : `/bills/${bill._id}`;
      const res = await apiClient.delete(url);
      const dj = res.data?.deleted_journal_entries || 0;
      toast.success(
        dj > 0
          ? `Eingangsrechnung gelöscht (${dj} Buchung/en mit entfernt).`
          : "Eingangsrechnung gelöscht."
      );
      await loadBills();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Löschen fehlgeschlagen.";
      toast.error(String(detail));
    }
  };

  const openPaymentDialog = (bill) => {
    setPaymentBill(bill);
    setPaymentForm({
      payment_date: bill.due_date,
      amount: bill.total,
      bank_account_id: "",
    });
  };

  const handlePayBill = async (e) => {
    e.preventDefault();
    if (!paymentBill) return;
    setPaying(true);
    setError("");
    try {
      await apiClient.post(`/bills/${paymentBill._id}/payments`, {
        payment_date: paymentForm.payment_date,
        amount: parseFloat(paymentForm.amount) || 0,
        bank_account_id: paymentForm.bank_account_id,
      }, { headers: { "Idempotency-Key": paymentIdemKey } });
      setPaymentIdemKey(crypto.randomUUID());
      setPaymentBill(null);
      await loadBills();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Fehler beim Erfassen der Zahlung.");
    } finally {
      setPaying(false);
    }
  };

  const supplierName = (id) => suppliers.find((s) => s._id === id)?.name || id;
  const shownBills = visibleBills.filter((b) => matchesSearch([b.bill_number, supplierName(b.supplier_id), b.issue_date, b.due_date, b.status, ...(b.lines || []).map((l) => l.description)], [b.total, b.subtotal], billSearch));

  // Eligible für Zahlungslauf: posted (also verbucht und offen) und noch keine Zahlung initiiert
  const isEligibleForBatch = (b) =>
    b.status === "posted" && !b.payment_batch_id;

  const eligibleBills = bills.filter(isEligibleForBatch);
  const eligibleIds = eligibleBills.map((b) => b._id);
  const allBillsSelected =
    bills.length > 0 && bills.every((b) => selectedIds.includes(b._id));
  const allEligibleSelected =
    eligibleIds.length > 0 && eligibleIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allBillsSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(bills.map((b) => b._id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectedBills = bills.filter((b) => selectedIds.includes(b._id));
  const selectedTotal = selectedBills.reduce(
    (sum, b) => sum + (parseFloat(b.total) || 0),
    0,
  );

  // Iter101: Bulk-Aktionen
  const [bulkBusy, setBulkBusy] = useState(false);
  const runBulkDeleteBills = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `${selectedIds.length} Eingangsrechnung(en) löschen? Verbuchte werden übersprungen.`
      )
    )
      return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/bills/bulk-delete`,
        { bill_ids: selectedIds }
      );
      const { succeeded, skipped, undo_token } = r.data;
      showBulkActionToast({
        message: `${succeeded} gelöscht${skipped ? ` · ${skipped} übersprungen` : ""}`,
        undoToken: undo_token,
        companyId,
        onUndo: loadBills,
      });
      setSelectedIds([]);
      loadBills();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Löschung fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };
  const runBulkMarkOpen = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/bills/bulk-status`,
        { bill_ids: selectedIds, status: "open" }
      );
      const { succeeded, skipped, undo_token } = r.data;
      showBulkActionToast({
        message: `${succeeded} als 'offen' markiert${skipped ? ` · ${skipped} übersprungen` : ""}`,
        undoToken: undo_token,
        companyId,
        onUndo: loadBills,
      });
      setSelectedIds([]);
      loadBills();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status-Update fehlgeschlagen.");
    } finally {
      setBulkBusy(false);
    }
  };
  const draftBillCount = selectedBills.filter((b) => b.status === "draft").length;

  // Iter109: MFTPF Bulk-Upload
  const [mftpfEnabled, setMftpfEnabled] = useState(false);
  const [mftpfBulkBusy, setMftpfBulkBusy] = useState(false);
  useEffect(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/mftpf-settings`)
      .then((r) => setMftpfEnabled(!!r.data?.enabled))
      .catch(() => setMftpfEnabled(false));
  }, [companyId]);

  const runMftpfBulkUpload = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(
      `${selectedIds.length} Eingangsrechnung(en) als Sammelauftrag (1 pain.001) ` +
      `an PostFinance MFTPF senden?\n\n` +
      `Tipp: Bills ohne IBAN oder QR-Referenz werden automatisch übersprungen.`,
    )) return;
    setMftpfBulkBusy(true);
    try {
      const r = await apiClient.post(
        `/companies/${companyId}/bills/mftpf/bulk-pain001`,
        { bill_ids: selectedIds },
      );
      const d = r.data;
      const modeBadge = d.mode === "mock" ? "Mock" : "Live";
      const skippedTxt = d.skipped_count > 0 ? ` (${d.skipped_count} übersprungen)` : "";
      toast.success(
        `✅ ${modeBadge}: Sammelauftrag mit ${d.transactions} Zahlungen ` +
        `(${d.total_amount.toLocaleString("de-CH", { style: "currency", currency: "CHF" })}) hochgeladen${skippedTxt}.`,
      );
      setSelectedIds([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "MFTPF-Sammelauftrag fehlgeschlagen");
    } finally {
      setMftpfBulkBusy(false);
    }
  };
  const lsvEligibleCount = selectedBills.filter(isEligibleForBatch).length;

  return (
    <div className="space-y-6" data-testid="bills-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          Eingangsrechnungen
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Lieferanten-Rechnungen — Status, Zahlungslauf und Archiv.
        </p>
      </div>

      <PaymentApprovalPanel companyId={companyId} onChanged={loadBills} />

      {/* Iter85: Hinweis-Banner — Erfassung erfolgt im Posteingang */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-indigo-200 bg-indigo-50/60 px-4 py-3 text-sm" data-testid="bills-inbox-hint">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="text-indigo-600 text-base flex-shrink-0">📥</span>
          <div className="min-w-0">
            <p className="font-medium text-indigo-900">Neue Eingangsrechnungen gehören in den Posteingang</p>
            <p className="text-xs text-indigo-700 mt-0.5">
              Diese Seite zeigt deine <b>bereits erfassten Eingangsrechnungen</b>. Upload &
              KI-Klassifikation laufen zentral im Posteingang.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => navigate("/inbox")}
          className="bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
          data-testid="bills-goto-inbox"
        >
          Zum Posteingang →
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card
        className="p-4 border border-slate-200 bg-white shadow-sm"
        data-testid="bills-list-card"
      >
        {selectedIds.length > 0 && (
          <div
            className="sticky top-0 z-10 -m-4 mb-3 flex flex-wrap items-center justify-between gap-3 rounded-t-md border-b border-emerald-200 bg-emerald-50 px-4 py-2.5"
            data-testid="bills-bulk-action-bar"
          >
            <div className="text-sm text-emerald-900">
              <span className="font-semibold">{selectedIds.length}</span> Rechnung(en) ausgewählt ·{" "}
              <span className="font-semibold">{selectedTotal.toFixed(2)} CHF</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedIds([])}
                data-testid="bills-clear-selection-button"
              >
                Auswahl aufheben
              </Button>
              {draftBillCount > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={runBulkMarkOpen}
                  disabled={bulkBusy}
                  data-testid="bills-bulk-mark-open"
                >
                  Als offen markieren ({draftBillCount})
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={runBulkDeleteBills}
                disabled={bulkBusy}
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
                data-testid="bills-bulk-delete"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Löschen
              </Button>
              {lsvEligibleCount > 0 && (
                <Button
                  type="button"
                  size="sm"
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-4"
                  onClick={() => setWizardOpen(true)}
                  data-testid="bills-payment-run-button"
                  title="Bündelt mehrere offene Rechnungen zu einem Zahlungsauftrag für die Bank"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Zahlungslauf erstellen ({lsvEligibleCount})
                </Button>
              )}
              {mftpfEnabled && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={mftpfBulkBusy}
                  onClick={runMftpfBulkUpload}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="bills-mftpf-bulk-button"
                  title="Alle ausgewählten Bills als 1 pain.001 (Sammelauftrag) an PostFinance MFTPF senden"
                >
                  <ServerCog className="h-3.5 w-3.5 mr-1" />
                  {mftpfBulkBusy ? "Lade…" : `Per MFTPF zahlen (${selectedIds.length})`}
                </Button>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-700">
            Alle Eingangsrechnungen
          </h2>
          <Button
            type="button"
            size="sm"
            className="rounded-full bg-slate-900 text-slate-50 px-4"
            data-testid="bills-add-button"
            onClick={() => navigate("/inbox")}
          >
            📥 Im Posteingang erfassen
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent
              className="max-w-2xl"
              data-testid="bills-dialog"
            >
              <DialogHeader>
                <DialogTitle>Neue Eingangsrechnung</DialogTitle>
              </DialogHeader>
              <form
                className="space-y-3"
                onSubmit={handleCreate}
                data-testid="bills-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>Lieferant</Label>
                    <Select
                      value={form.supplier_id}
                      onValueChange={(value) =>
                        setForm({ ...form, supplier_id: value })
                      }
                    >
                      <SelectTrigger data-testid="bills-supplier-select">
                        <SelectValue placeholder="Lieferant wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((s) => (
                          <SelectItem key={s._id} value={s._id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="bill-number">Rechnungsnummer</Label>
                    <Input
                      id="bill-number"
                      value={form.bill_number}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          bill_number: e.target.value,
                        })
                      }
                      data-testid="bills-number-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="issue-date">Rechnungsdatum</Label>
                    <SwissDateField
                      id="issue-date"
                      
                      value={form.issue_date}
                      onChange={(e) =>
                        setForm({ ...form, issue_date: e.target.value })
                      }
                      data-testid="bills-issue-date-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="due-date">Fälligkeitsdatum</Label>
                    <SwissDateField
                      id="due-date"
                      
                      value={form.due_date}
                      onChange={(e) =>
                        setForm({ ...form, due_date: e.target.value })
                      }
                      data-testid="bills-due-date-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="vat-rate">MWST-Satz (%)</Label>
                    <Input
                      id="vat-rate"
                      type="number"
                      step="0.1"
                      value={form.vat_rate}
                      onChange={(e) => {
                        const next = recalcTotals({
                          ...form,
                          vat_rate: e.target.value,
                        });
                        setForm(next);
                      }}
                      data-testid="bills-vat-rate-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-medium text-slate-600">
                    Positionen
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Beschreibung</TableHead>
                        <TableHead>Betrag</TableHead>
                        <TableHead>Konto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.line_items.map((li, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Input
                              value={li.description}
                              onChange={(e) =>
                                handleLineChange(
                                  index,
                                  "description",
                                  e.target.value,
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={li.amount}
                              onChange={(e) =>
                                handleLineChange(
                                  index,
                                  "amount",
                                  e.target.value,
                                )
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={li.account_id}
                              onValueChange={(value) =>
                                handleLineChange(
                                  index,
                                  "account_id",
                                  value,
                                )
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Konto wählen" />
                              </SelectTrigger>
                              <SelectContent>
                                {accounts.map((acc) => (
                                  <SelectItem
                                    key={acc._id}
                                    value={acc.code}
                                  >
                                    {acc.code} · {acc.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLineItem}
                    className="rounded-full text-xs"
                  >
                    Position hinzufügen
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="md:col-start-2 space-y-1">
                    <div className="flex justify-between text-slate-600">
                      <span>Netto (ohne MWST)</span>
                      <span>
                        {form.subtotal.toFixed(2)} {form.currency}
                      </span>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>MWST</span>
                      <span>
                        {form.tax_amount.toFixed(2)} {form.currency}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-slate-900 border-t pt-1">
                      <span>Total</span>
                      <span>
                        {form.total.toFixed(2)} {form.currency}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setDialogOpen(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    className="rounded-full bg-slate-900 text-slate-50 px-4"
                    disabled={saving}
                    data-testid="bills-save-button"
                  >
                    {saving
                      ? "Speichern ..."
                      : "Eingangsrechnung speichern"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <ListSearch value={billSearch} onChange={setBillSearch} count={shownBills.length} testid="bills-search" className="order-last ml-auto w-full sm:w-72" placeholder="Suchen: Lieferant, Nr., Betrag, Datum …" />
          <StatusFilterPills testidPrefix="bills" value={billFilter.value} onChange={billFilter.setValue} def={billFilter.def}
            options={BILL_STATUS.filter(([k]) => k === "all" || bills.some((b) => b.status === k) || k === billFilter.value).map(([key, label]) => ({ key, label, count: key === "all" ? bills.length : bills.filter((b) => b.status === key).length }))} />
        </div>
        <Table data-testid="bills-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allBillsSelected}
                  onCheckedChange={toggleSelectAll}
                  disabled={bills.length === 0}
                  aria-label="Alle auswählen"
                  data-testid="bills-select-all-checkbox"
                />
              </TableHead>
              <TableHead>Rechnungsdatum</TableHead>
              <TableHead>Lieferant</TableHead>
              <TableHead>Rechnungsnummer</TableHead>
              <TableHead>Fälligkeit</TableHead>
              <TableHead>Betrag</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bills.length > 0 && shownBills.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-slate-500 py-6" data-testid="bills-filter-empty">Keine Eingangsrechnungen mit diesem Status.</TableCell></TableRow>
            )}
            {bills.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <EmptyState
                    testid="bills-empty-state"
                    icon={<Receipt className="h-5 w-5" />}
                    title="Noch keine Eingangsrechnungen"
                    description="Erfassen Sie eine Kreditoren-Rechnung oder laden Sie Belege hoch — mit OCR und automatischer Verbuchung."
                  />
                </TableCell>
              </TableRow>
            )}
            {shownBills.map((b) => (
              <TableRow
                key={b._id}
                data-testid={`bills-row-${b._id}`}
                data-highlight-id={b._id}
                className={selectedIds.includes(b._id) ? "bg-emerald-50/50" : ""}
              >
                <TableCell>
                  <Checkbox
                    checked={selectedIds.includes(b._id)}
                    onCheckedChange={() => toggleSelect(b._id)}
                    aria-label={`Rechnung ${b.bill_number} auswählen${isEligibleForBatch(b) ? " (zahlungsfähig)" : ""}`}
                    data-testid={`bills-select-checkbox-${b._id}`}
                  />
                </TableCell>
                <TableCell>{formatDate(b.issue_date)}</TableCell>
                <TableCell>{supplierName(b.supplier_id)}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5">
                    <span>{b.bill_number}</span>
                    <BillRowActions bill={b} onOpen={billPreview.open} loading={billPreview.loading} onAddAttachment={setAttachBill} />
                    {b.warranty_id && (
                      <Link to={`/warranties?highlight=${b.warranty_id}`} data-testid={`bill-warranty-${b._id}`}
                        className="inline-flex items-center gap-1 rounded border border-violet-200 bg-violet-50 px-1.5 py-px text-[10px] text-violet-800 hover:bg-violet-100"
                        title={b.warranty_expiry ? `Garantie bis ${formatDate(b.warranty_expiry)} – im Garantiebereich öffnen` : "Garantie im Garantiebereich öffnen"}>
                        <Shield className="h-3 w-3" /> Garantie
                      </Link>
                    )}
                  </span>
                </TableCell>
                <TableCell>{formatDate(b.due_date)}</TableCell>
                <TableCell>
                  {b.total.toFixed(2)} {b.currency}
                </TableCell>
                <TableCell>
                  {({ draft: "Entwurf", open: "Offen", posted: "Verbucht", partially_paid: "Teilbezahlt", paid: "Bezahlt", overdue: "Überfällig" })[b.status] || b.status}
                  <BillPaymentPlan bill={b} onEdit={setPlanBill} />
                  {b.paid_date && (
                    <div className="text-[11px] text-emerald-700 normal-case" data-testid={`bill-paid-date-${b._id}`}>
                      Bezahlt am {formatDate(b.paid_date)}{PAID_SOURCE_LABEL[b.paid_date_source] ? ` (${PAID_SOURCE_LABEL[b.paid_date_source]})` : ""}
                      {b.debited_date && b.debited_date !== b.paid_date && (
                        <span className="text-slate-500" data-testid={`bill-debited-date-${b._id}`}> · Belastet {formatDate(b.debited_date)}</span>
                      )}
                    </div>
                  )}
                  {b.payment_batch_id && (
                    <Badge
                      variant="outline"
                      className="ml-1 border-sky-300 bg-sky-50 text-sky-800 text-[10px] cursor-pointer hover:bg-sky-100"
                      data-testid={`bills-payment-initiated-badge-${b._id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetailBatchId(b.payment_batch_id);
                      }}
                      title="Klicken für Batch-Details"
                    >
                      Zahlung initiiert
                    </Badge>
                  )}
                  {b.payment_rejected_reason && (
                    <Badge
                      variant="outline"
                      className="ml-1 border-rose-300 bg-rose-50 text-rose-800 text-[10px]"
                      data-testid={`bills-payment-rejected-badge-${b._id}`}
                      title={b.payment_rejected_reason}
                    >
                      Abgelehnt
                    </Badge>
                  )}
                  {b.paid_via === "pain002" && (
                    <Badge
                      variant="outline"
                      className="ml-1 border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px]"
                      data-testid={`bills-paid-via-pain002-${b._id}`}
                      title="Auto-bezahlt via PostFinance pain.002 Statusrückmeldung"
                    >
                      Auto-Buchung
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  {(b.status === "open" || (b.status === "partially_paid" && !b.journal_entry_id)) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handlePostBill(b._id)}
                      disabled={posting}
                      data-testid={`bills-post-button-${b._id}`}
                    >
                      Buchen
                    </Button>
                  )}
                  {b.status === "posted" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openPaymentDialog(b)}
                      disabled={paying}
                      data-testid={`bills-pay-button-${b._id}`}
                      title="Eintragen, dass diese Rechnung bezahlt wurde (falls nicht automatisch über den Bank-Abgleich erkannt)"
                    >
                      Zahlung erfassen
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-1"
                    onClick={() => deleteBill(b)}
                    data-testid={`bills-delete-button-${b._id}`}
                    title="Eingangsrechnung löschen"
                  >
                    Löschen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <PaymentPlanDialog open={!!planBill} onOpenChange={(o) => { if (!o) setPlanBill(null); }} bill={planBill} onSaved={loadBills} />

      <BillPreviewDialog
        preview={billPreview.preview}
        onClose={billPreview.close}
        onSwitch={billPreview.open}
        onRemove={(bill, fileId) => billPreview.removeAttachment(bill, fileId, replaceBill)}
        onSetKind={(bill, fileId, kind) => billPreview.setKind(bill, fileId, kind, replaceBill)}
        onClassify={(bill) => billPreview.classify(bill, replaceBill)}
        onBillUpdated={(b) => { replaceBill(b); billPreview.updateBill(b); }}
        onAddAttachment={(bill) => { billPreview.close(); setAttachBill(bill); }}
      />
      <BillAttachmentUploadDialog bill={attachBill} onClose={() => setAttachBill(null)} onUploaded={replaceBill} />

      {paymentBill && (
        <Dialog
          open={!!paymentBill}
          onOpenChange={(open) => !open && setPaymentBill(null)}
        >
          <DialogContent
            className="max-w-md"
            data-testid="bills-payment-dialog"
          >
            <DialogHeader>
              <DialogTitle>
                Zahlung erfassen: {paymentBill.bill_number}
              </DialogTitle>
            </DialogHeader>
            <form className="space-y-3" onSubmit={handlePayBill}>
              <div className="space-y-1">
                <Label htmlFor="payment-date">Zahlungsdatum</Label>
                <SwissDateField
                  id="payment-date"
                  
                  value={paymentForm.payment_date}
                  onChange={(e) =>
                    setPaymentForm({
                      ...paymentForm,
                      payment_date: e.target.value,
                    })
                  }
                  required
                  data-testid="bills-payment-date-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="payment-amount">Betrag</Label>
                <Input
                  id="payment-amount"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) =>
                    setPaymentForm({
                      ...paymentForm,
                      amount: e.target.value,
                    })
                  }
                  required
                  data-testid="bills-payment-amount-input"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="payment-bank">Bankkonto</Label>
                <Select
                  value={paymentForm.bank_account_id}
                  onValueChange={(value) =>
                    setPaymentForm({
                      ...paymentForm,
                      bank_account_id: value,
                    })
                  }
                >
                  <SelectTrigger
                    id="payment-bank"
                    data-testid="bills-payment-bank-select"
                  >
                    <SelectValue placeholder="Bankkonto wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((acc) => (
                      <SelectItem key={acc._id} value={acc.code}>
                        {acc.code} · {acc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPaymentBill(null)}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  className="rounded-full bg-slate-900 text-slate-50 px-4"
                  disabled={paying}
                  data-testid="bills-payment-save-button"
                >
                  {paying ? "Speichern ..." : "Zahlung buchen"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <PaymentRunWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        companyId={companyId}
        selectedBills={selectedBills}
        suppliers={suppliers}
        onSuccess={async () => {
          setSelectedIds([]);
          await loadBills();
        }}
      />

      <BatchDetailModal
        open={!!detailBatchId}
        onClose={() => setDetailBatchId(null)}
        companyId={companyId}
        batchId={detailBatchId}
        batchType="payment"
      />
    </div>
  );
}
